import React from 'react';
import { useLast10 } from '../../contexts/Last10Context';

/**
 * FinishScore
 *
 * A compact dashboard widget surfacing the user's Last 10% execution metrics.
 * Designed for professional contexts — no badges, no confetti, just data.
 */
const FinishScore = () => {
    const { finishScore, loadingScore } = useLast10();

    const { score, totalEnteredLast10, totalCompletedFromLast10, totalAbandonedInLast10 } = finishScore;

    const scoreLabel =
        score === null
            ? '—'
            : `${score}%`;

    const scoreSubtext =
        score === null
            ? 'No data yet'
            : score >= 80
                ? 'Strong finisher'
                : score >= 50
                    ? 'Improving'
                    : 'Needs attention';

    const scoreColor =
        score === null
            ? 'text-gray-400 dark:text-gray-500'
            : score >= 80
                ? 'text-emerald-600 dark:text-emerald-400'
                : score >= 50
                    ? 'text-amber-600 dark:text-amber-400'
                    : 'text-red-600 dark:text-red-400';

    if (loadingScore) {
        return (
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-3" />
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/4 mb-2" />
                <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-5 border border-gray-100 dark:border-gray-700">
            {/* Header */}
            <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-5 bg-amber-500 rounded-full" />
                <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                    Finish Score
                </h3>
            </div>

            {/* Primary metric */}
            <div className="flex items-end gap-3 mb-4">
                <span className={`text-4xl font-bold tabular-nums ${scoreColor}`}>
                    {scoreLabel}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400 mb-1">
                    {scoreSubtext}
                </span>
            </div>

            {/* Supporting stats */}
            <div className="grid grid-cols-3 gap-3 pt-4 border-t border-gray-100 dark:border-gray-700">
                <Stat
                    label="Entered Final Phase"
                    value={totalEnteredLast10}
                    colorClass="text-gray-800 dark:text-gray-200"
                />
                <Stat
                    label="Completed"
                    value={totalCompletedFromLast10}
                    colorClass="text-emerald-700 dark:text-emerald-400"
                />
                <Stat
                    label="Abandoned"
                    value={totalAbandonedInLast10}
                    colorClass={totalAbandonedInLast10 > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-400 dark:text-gray-500'}
                />
            </div>

            {/* Contextual note */}
            {totalEnteredLast10 === 0 && (
                <p className="mt-4 text-xs text-gray-400 dark:text-gray-500 leading-relaxed">
                    Your Finish Score activates once a goal reaches 80% completion.
                </p>
            )}
        </div>
    );
};

const Stat = ({ label, value, colorClass }) => (
    <div className="text-center">
        <p className={`text-2xl font-bold tabular-nums ${colorClass}`}>{value}</p>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-tight">{label}</p>
    </div>
);

export default FinishScore;
